import { type Component, defineAsyncComponent } from 'vue'
import type {
  FeatureGuardConfig,
  RouteRecordRaw,
  TierGuardConfig,
  GenericGuardConfig,
  RouteMeta,
  RouteLocation,
} from 'vue-router'
import { guardComponentForType } from '/@/router/utils'
import classicRoutes from '/@/pages/_DEV_/Classic/routes'
import { stellaRoutes } from '/@/pages/_DEV_/Stella/routes'
import type { TierFeature } from '/@/common/tiers'

/*
  NOTE: Changing any routes below requires an update to server compiled routes.
  Use the `pnpm redirects:update` command.
*/

/* eslint-disable @typescript-eslint/no-explicit-any */
interface ExternalData {
  redirects: {
    CompleteClass: () => Component<any, any, any>
    PortfolioRedirect: (id: string) => Component<any, any, any>
    ViziRedirect: (id: string) => Component<any, any, any>
    ProjectRedirect: (id: string) => Component<any, any, any>
    PaletteRedirect: (id: string) => Component<any, any, any>
    HelpHubRedirect: () => Component<any, any, any>
    CommunityRedirect: () => Component<any, any, any>
  }
  DesignerRootPage: Component<any, any, any>
  isSm: boolean
}
/* eslint-enable */

function createGuardedRoute(
  routeConfig: Omit<RouteRecordRaw, 'component'>,
  guardConfigs: Array<
    TierGuardConfig | GenericGuardConfig | FeatureGuardConfig
  >,
) {
  const guardComponent = guardConfigs.length
    ? guardComponentForType(guardConfigs[0].type)
    : null

  return {
    ...routeConfig,
    meta: {
      ...routeConfig.meta,
      guards: guardConfigs,
    },
    component: guardComponent,
  } as RouteRecordRaw
}

function createProjectRoute() {
  const meta: RouteMeta = {
    pageName: 'Project',
    hasBranding: true,
    navType: 'shy',
    navTheme: 'dark',
    noAuth: true,
    stellaNavType: 'custom',
    theme: 'stella',
    fullWidth: true,
    footerType: 'none',
  }
  return createGuardedRoute(
    {
      path: 'project/:projectId',
      props: true,
      meta,
      children: [
        {
          path: 'cover',
          name: 'project-view-cover',
          meta,
          component: () =>
            import('../pages/Project/common/elements/CoverSection.vue'),
        },
        {
          path: 'budget',
          name: 'project-view-budget',
          meta,
          component: () =>
            import('../pages/Project/common/elements/BudgetSection.vue'),
        },
        {
          path: 'feedback',
          name: 'project-view-feedback',
          meta,
          component: () =>
            import('../pages/Project/common/elements/FeedbackSection.vue'),
        },
        {
          path: 'people',
          name: 'project-view-people',
          meta,
          component: () =>
            import('../pages/Project/common/elements/PeopleSection.vue'),
        },
        {
          path: 'pro-settings',
          name: 'project-view-pro-settings',
          meta,
          component: () =>
            import('../pages/Project/common/elements/ProSettingsSection.vue'),
        },
        {
          path: 'settings',
          name: 'project-view-settings',
          meta,
          component: () =>
            import('../pages/Project/common/elements/SettingsSection.vue'),
        },
        {
          path: 'section/:sectionId',
          name: 'project-view-section',
          meta,
          component: () =>
            import('../pages/Project/common/elements/Section.vue'),
        },
        {
          path: '',
          name: 'project-view',
          meta,
          component: () =>
            import('../pages/Project/common/elements/Section.vue'),
        },
      ],
    },
    [
      {
        type: 'project-guard',
        allowComponent: defineAsyncComponent(
          () => import('../pages/Project/View/Page.vue'),
        ),
        denyComponent: defineAsyncComponent(
          () => import('../pages/Blank/BlankLoading.vue'),
        ),
        loadingComponent: defineAsyncComponent(
          () => import('../pages/Blank/BlankLoading.vue'),
        ),
      },
    ],
  )
}

function createProjectEditRoute() {
  const meta: RouteMeta = {
    pageName: 'Project Editor',
    noAuth: true,
    mode: 'edit',
    navType: 'shy',
    stellaNavType: 'custom',
    theme: 'stella',
    fullWidth: true,
    footerType: 'none',
  }
  return createGuardedRoute(
    {
      path: '/project/:projectId/editor',
      alias: ['/project/:projectId/edit'],
      props: true,
      meta,
      children: [
        {
          path: 'cover',
          name: 'project-edit-cover',
          meta,
          component: () =>
            import('../pages/Project/common/elements/CoverSection.vue'),
        },
        {
          path: 'budget',
          name: 'project-edit-budget',
          meta,
          component: () =>
            import('../pages/Project/common/elements/BudgetSection.vue'),
        },
        {
          path: 'feedback',
          name: 'project-edit-feedback',
          meta,
          component: () =>
            import('../pages/Project/common/elements/FeedbackSection.vue'),
        },
        {
          path: 'people',
          name: 'project-edit-people',
          meta,
          component: () =>
            import('../pages/Project/common/elements/PeopleSection.vue'),
        },
        {
          path: 'pro-settings',
          name: 'project-edit-pro-settings',
          meta,
          component: () =>
            import('../pages/Project/common/elements/ProSettingsSection.vue'),
        },
        {
          path: 'settings',
          name: 'project-edit-settings',
          meta,
          component: () =>
            import('../pages/Project/common/elements/SettingsSection.vue'),
        },
        {
          path: 'section/:sectionId',
          name: 'project-edit-section',
          meta,
          component: () =>
            import('../pages/Project/common/elements/Section.vue'),
        },
        {
          path: 'thingdex',
          name: 'project-edit-thingdex',
          meta,
          component: () =>
            import('../pages/Project/common/elements/Section.vue'),
        },
        {
          path: '',
          name: 'project-edit',
          meta,
          redirect: (to: RouteLocation) => ({
            name: 'project-edit-thingdex',
            params: to.params,
          }),
        },
      ],
    },
    [
      {
        type: 'project-guard',
        allowComponent: defineAsyncComponent(
          () => import('../pages/Project/Edit/Page.vue'),
        ),
        denyComponent: defineAsyncComponent(
          () => import('../pages/Project/View/Page.vue'),
        ),
        loadingComponent: defineAsyncComponent(
          () => import('../pages/Blank/BlankLoading.vue'),
        ),
      },
    ],
  )
}

function createSettingsRoute() {
  const meta: RouteMeta = {
    pageName: 'Settings',
    theme: 'stella',
    fullWidth: true,
  }

  return {
    path: '/settings',
    meta,
    children: [
      {
        path: 'profile',
        name: 'settings-profile',
        meta,
        component: () => import('../pages/Settings/Profile/Panel.vue'),
      },
      {
        path: 'branding',
        name: 'settings-branding',
        meta,
        component: () => import('../pages/Settings/Branding/Panel.vue'),
      },
      {
        path: 'billing',
        name: 'settings-billing',
        alias: ['/settings/subscription'],
        meta,
        component: () => import('../pages/Settings/Subscription/Panel.vue'),
      },
      {
        path: 'team',
        name: 'settings-team',
        meta,
        component: () => import('../pages/Settings/Team/Panel.vue'),
      },
      {
        path: 'referral',
        name: 'settings-referral',
        alias: ['/settings/invite'],
        meta,
        component: () => import('../pages/Settings/Referral/Panel.vue'),
      },
      {
        path: 'notifications',
        name: 'settings-notifications',
        meta,
        component: () => import('../pages/Settings/Notifications/Panel.vue'),
      },
      {
        path: '',
        name: 'settings',
        meta,
        redirect: (to: RouteLocation) => ({
          name: 'settings-profile',
          params: to.params,
        }),
      },
    ],
    component: () => import('../pages/Settings/Page.vue'),
  }
}

function createUpgradeTierGuard(
  tierFeature: TierFeature,
  allowComponent: ReturnType<typeof defineAsyncComponent>,
): TierGuardConfig {
  return {
    type: 'tier-guard',
    tierFeature,
    allowComponent,
    denyComponent: defineAsyncComponent(
      () => import('../pages/Blank/NeedUpgrade.vue'),
    ),
    loadingComponent: defineAsyncComponent(
      () => import('../pages/Blank/BlankLoading.vue'),
    ),
  }
}

function createSchoolRoute(): RouteRecordRaw {
  return {
    path: '/learn',
    name: 'learn-root',
    meta: {
      pageName: 'Learn',
      theme: 'stella',
      fullWidth: true,
    },
    component: defineAsyncComponent(() => import('../pages/Learn/Page.vue')),
    children: [
      {
        path: 'tips',
        name: 'learn-tips',
        alias: ['/tips'],
        meta: {
          pageName: 'Tips',
          theme: 'stella',
          fullWidth: true,
        },
        component: defineAsyncComponent(
          () => import('../pages/Learn/TipsPage.vue'),
        ),
      },
      createGuardedRoute(
        {
          path: 'class',
          name: 'learn-class',
          alias: ['/school', '/class'],
          meta: {
            pageName: 'Class',
            theme: 'stella',
            fullWidth: true,
          },
        },
        [
          createUpgradeTierGuard(
            'DESIGN_CLASSES',
            defineAsyncComponent(() => import('../pages/Learn/SchoolPage.vue')),
          ),
        ],
      ),
      {
        path: '',
        name: 'learn',
        meta: {
          pageName: 'Learn Tips',
          theme: 'stella',
          fullWidth: true,
        },
        redirect: { name: 'learn-tips' },
      },
    ],
  }
}

export async function getRoutes({ redirects, DesignerRootPage }: ExternalData) {
  const routes: Array<RouteRecordRaw> = [
    {
      name: 'designer-root-route',
      path: '/u/:nickname',
      props: true,
      meta: {
        pageName: '',
        noAuth: true,
      },
      component: DesignerRootPage,
      children: [
        {
          path: 'vizi/:viziId',
          name: 'vizi-view',
          props: true,
          component: () => import('../pages/Vizi/View/Page.vue'),
          meta: {
            pageName: 'Vizi',
            hasBranding: true,
            noAuth: true,
            noAnalytics: true,
            stellaNavType: 'custom',
            navTheme: 'dark',
            theme: 'stella',
            fullWidth: true,
            footerType: 'none',
          },
        },
        {
          path: 'collection/:id',
          name: 'my-things-collection-view',
          meta: {
            pageName: 'My Things Collection Presentation',
            noAuth: true,
            stellaNavType: 'custom',
            navTheme: 'dark',
            theme: 'stella',
            fullWidth: true,
            footerType: 'none',
          },
          component: defineAsyncComponent(
            () => import('../pages/MyThings/Page.vue'),
          ),
        },
        // note: ↓ ↓ ↓ in case we want to open My Things presentation to the public ↓ ↓ ↓
        // {
        //   path: 'things',
        //   name: 'my-things-view',
        //   component: () => import('../pages/MyThings/Page.vue'),
        //   meta: {
        //     pageName: 'My Things Presentation',
        //     noAuth: true,
        //     stellaNavType: 'custom',
        //     navTheme: 'dark',
        //     theme: 'stella',
        //     fullWidth: true,
        //     footerType: 'none',
        //   },
        //   children: [
        //     {
        //       path: ':id',
        //       name: 'my-things-collection-view',
        //       meta: {
        //         pageName: 'My Things Collection Presentation',
        //         noAuth: true,
        //         stellaNavType: 'custom',
        //         navTheme: 'dark',
        //         theme: 'stella',
        //         fullWidth: true,
        //         footerType: 'none',
        //       },
        //       component: defineAsyncComponent(
        //         () => import('../pages/MyThings/Page.vue'),
        //       ),
        //     },
        //   ],
        // },

        createProjectRoute(),
        {
          path: 'palette/:paletteId',
          name: 'palette-view',
          props: true,
          meta: {
            pageName: 'Palette Details',
            hasBranding: true,
            noAuth: true,
            stellaNavType: 'custom',
            navTheme: 'dark',
            theme: 'stella',
            fullWidth: true,
            footerType: 'none',
          },
          component: () => import('../pages/TDP/Palette.vue'),
        },
        {
          name: 'designer-profile-redirect',
          path: '',
          redirect: to => `/u/${to.params.nickname}/profile`,
          meta: { pageName: '', noAuth: true },
        },
        {
          path: 'portfolio',
          name: 'portfolio',
          redirect: to => `/u/${to.params.nickname}/profile`,
          meta: { pageName: '', noAuth: true },
        },
        {
          path: 'profile',
          name: 'profile',
          props: true,
          meta: {
            pageName: 'Profile',
            noAuth: true,
            theme: 'stella',
            fullWidth: true,
          },
          component: () => import('../pages/Portfolio/Page.vue'),
        },
        {
          path: 'designer-follows',
          name: 'designer-follows',
          redirect: to => `/u/${to.params.nickname}/follows`,
          meta: { pageName: '', noAuth: true },
        },
        {
          path: 'follows',
          name: 'follows',
          props: true,
          meta: {
            pageName: 'Follows',
            noAuth: true,
            theme: 'stella',
            fullWidth: true,
          },
          component: () => import('../pages/Portfolio/Follows/Page.vue'),
        },
        {
          path: 'portfolio/project/:portfolioProjectId',
          name: 'portfolio-project-redirect',
          redirect: to =>
            `/u/${to.params.nickname}/profile/project/${to.params.portfolioProjectId}`,
          meta: { pageName: '', noAuth: true },
        },
        {
          path: 'profile/project/:portfolioProjectId',
          name: 'portfolio-project',
          props: true,
          meta: {
            pageName: 'Portfolio Project',
            noAuth: true,
            // hasBranding: true,
          },
          component: () => import('../pages/Portfolio/ProjectPage.vue'),
        },
      ],
    },
    createGuardedRoute(
      {
        path: '/vizi/create',
        name: 'vizi-create',
        meta: {
          pageName: '',
          noAuth: false,
          stellaNavType: 'custom',
          theme: 'stella',
          fullWidth: true,
        },
      },
      [
        {
          type: 'tier-guard',
          tierFeature: 'CREATE_VIZIS',
          allowComponent: defineAsyncComponent(
            () => import('../pages/Vizi/Create.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/Blank/NeedUpgrade.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    {
      path: '/vizi/demo-select',
      name: 'vizi-demo-select',
      meta: {
        pageName: 'Viz Demo Select',
        noAuth: true,
        noAnalytics: true,
        theme: 'stella',
        fullWidth: true,
        stellaNavType: 'custom',
        footerType: 'none',
      },
      component: () =>
        import('../pages/Vizi/Edit/api/demo/elements/DemoSelectionPage.vue'),
    },
    createGuardedRoute(
      {
        path: '/vizi/:viziId/edit',
        name: 'vizi-edit',
        props: true,
        meta: {
          pageName: 'Viz',
          noAuth: true,
          noAnalytics: true,
          navType: 'shy',
          stellaNavType: 'custom',
          mode: 'edit',
          theme: 'stella',
          fullWidth: true,
          footerType: 'none',
        },
        alias: [
          //
          '/floorplan/:viziId/edit',
          '/editorial/:viziId/edit',
          '/vizi/demo',
        ],
      },
      [
        {
          type: 'mockup-guard',
          allowComponent: defineAsyncComponent(
            () => import('../pages/Vizi/Edit/Page.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/Vizi/View/Page.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    createGuardedRoute(
      {
        path: '/project/create',
        name: 'project-create',
        meta: {
          pageName: 'Create Project',
          noAuth: true,
          stellaNavType: 'custom',
          theme: 'stella',
        },
      },
      [
        {
          type: 'tier-guard',
          tierFeature: 'CREATE_PROJECTS',
          allowComponent: defineAsyncComponent(
            () => import('../pages/Project/Create/Page.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/Blank/NeedUpgrade.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    createProjectEditRoute(),
    {
      path: '/palette/demo-create',
      name: 'palette-create-demo',
      props: true,
      meta: {
        pageName: 'Color Pal Create Demo',
        noAnalytics: true,
        noAuth: true,
        footerType: 'none',
        mode: 'edit',
        stellaNavType: 'custom',
        theme: 'stella',
        fullWidth: true,
      },
      component: () => import('../pages/Palette/Create.vue'),
    },
    createGuardedRoute(
      {
        path: '/palette/create',
        name: 'palette-create',
        meta: {
          pageName: '',
          noAuth: true,
          stellaNavType: 'custom',
          theme: 'stella',
          fullWidth: true,
        },
      },
      [
        {
          type: 'tier-guard',
          tierFeature: 'CREATE_PALETTES',
          allowComponent: defineAsyncComponent(
            () => import('../pages/Palette/Create.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/Blank/NeedUpgrade.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    {
      path: '/palette/:paletteId/demo',
      name: 'palette-edit-demo',
      props: true,
      meta: {
        pageName: 'Color Pal Edit Demo',
        noAnalytics: true,
        noAuth: true,
        footerType: 'none',
        mode: 'edit',
        stellaNavType: 'custom',
        theme: 'stella',
        fullWidth: true,
      },
      component: () => import('../pages/Palette/Page.vue'),
    },
    createGuardedRoute(
      {
        path: '/palette/:paletteId/edit',
        name: 'palette-edit',
        props: true,
        meta: {
          pageName: 'Color Pal',
          noAuth: true,
          footerType: 'none',
          mode: 'edit',
          stellaNavType: 'custom',
          theme: 'stella',
          fullWidth: true,
        },
      },
      [
        {
          type: 'palette-guard',
          allowComponent: defineAsyncComponent(
            () => import('../pages/Palette/Page.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/TDP/Palette.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    {
      path: '/blank',
      name: 'blank-page',
      meta: {
        pageName: 'Blank Page',
        noAnalytics: true,
        noAuth: true,
      },
      component: () => import('../pages/Blank/Page.vue'),
    },
    {
      path: '/404',
      name: '404',
      meta: {
        pageName: 'Not Found',
        noAnalytics: true,
        noAuth: true,
        theme: 'stella',
      },
      component: () => import('../pages/Blank/404.vue'),
    },
    {
      path: '/source',
      name: 'source',
      alias: ['/thingology', '/search', '/explore'],
      meta: {
        pageName: 'Source',
        navType: 'shy',
        theme: 'stella',
        fullWidth: true,
        noAuth: true,
      },
      component: () => import('../pages/Discover/Page.vue'),
    },
    {
      path: '/thingology/for-you',
      name: 'for-you',
      alias: ['/for-you', '/fyp'],
      redirect: { name: 'source', query: { clear: '1' } },
    },
    {
      path: '/brand/:vendorId',
      name: 'brand',
      props: true,
      meta: {
        pageName: 'Brand',
        theme: 'stella',
        fullWidth: true,
        noAuth: true,
      },
      component: () => import('../pages/Discover/Page.vue'),
    },
    {
      path: '/saved',
      name: 'saved-things',
      alias: ['/saved-things', '/bookmarks'],
      redirect: '/things',
    },
    {
      path: '/saved/:identifier',
      name: 'saved-identifier',
      props: true,
      meta: {
        pageName: 'Shared Saved Things',
        theme: 'stella',
        fullWidth: true,
        noAuth: true,
      },
      component: () => import('../pages/Bookmarks/DesignerPage.vue'),
    },
    createGuardedRoute(
      {
        path: '/things',
        name: 'my-things',
        alias: ['/my-things', '/mythings'],
        meta: {
          pageName: 'My Things',
          theme: 'stella',
          navType: 'shy',
          stellaNavType: 'custom',
          fullWidth: true,
          mode: 'edit',
          footerType: 'none',
        },
        children: [
          {
            path: ':id',
            name: 'my-things-collection',
            meta: {
              pageName: 'My Things Collection',
              theme: 'stella',
              navType: 'shy',
              stellaNavType: 'custom',
              fullWidth: true,
              mode: 'edit',
              footerType: 'none',
            },
            component: defineAsyncComponent(
              () => import('../pages/MyThings/Page.vue'),
            ),
          },
        ],
      },
      [
        {
          type: 'tier-guard',
          tierFeature: 'BOOKMARK',
          allowComponent: defineAsyncComponent(
            () => import('../pages/MyThings/Page.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/Blank/NeedUpgrade.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    {
      path: '/sessions',
      name: 'sessions',
      meta: {
        pageName: 'Spoak Sessions Page',
      },
      component: () => import('../pages/Sessions/Page.vue'),
    },
    {
      path: '/',
      name: 'home',
      meta: {
        pageName: 'Home',
        theme: 'stella',
        fullWidth: true,
      },
      component: () => import('../pages/Home/Page.vue'),
    },
    {
      path: '/class/goal/:goalId',
      name: 'goal',
      alias: ['/goal/:goalId'],
      meta: {
        pageName: 'Goal',
      },
      props: true,
      component: () => import('../pages/Goal/Page.vue'),
    },
    {
      path: '/class/:classId',
      name: 'class',
      meta: {
        pageName: 'Class',
      },
      props: true,
      component: () => import('../pages/Class/Page.vue'),
    },
    {
      path: '/class/:designerClassId/completion',
      name: 'class-completion',
      meta: {
        pageName: 'Class Completion',
        noAuth: true,
      },
      props: true,
      component: () => import('../pages/Class/Completion.vue'),
    },
    {
      path: '/class/:classId/complete',
      name: 'complete-class',
      props: true,
      meta: { pageName: 'Class Completion' },
      component: redirects.CompleteClass(),
    },
    createSchoolRoute(),
    {
      path: '/jobs',
      name: 'jobs',
      meta: {
        pageName: 'Jobs Fair',
        theme: 'stella',
        fullWidth: true,
      },
      component: () => import('../pages/Jobs/Page.vue'),
    },
    createSettingsRoute(),
    {
      path: '/services',
      name: 'services',
      meta: {
        pageName: 'Services',
        theme: 'stella',
        fullWidth: true,
      },
      component: () => import('../pages/Services/Page.vue'),
    },
    {
      path: '/client/:clientId',
      name: 'client-profile',
      props: true,
      meta: {
        pageName: 'Client Profile',
      },
      component: () => import('../pages/Client/Page.vue'),
    },
    createGuardedRoute(
      {
        path: '/designs',
        name: 'designs',
        alias: ['/dashboard'],
        meta: {
          pageName: 'My Designs',
          theme: 'stella',
          fullWidth: true,
        },
      },
      [
        {
          type: 'tier-guard',
          tierFeature: 'VIEW_DESIGNS',
          allowComponent: defineAsyncComponent(
            () => import('../pages/Dashboard/Page.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/Blank/NeedUpgrade.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    {
      name: 'portfolio-redirect',
      path: '/portfolio',
      meta: {
        pageName: '',
        noAuth: true,
      },
      component: redirects.PortfolioRedirect('profile'),
    },
    {
      name: 'profile-redirect',
      path: '/profile',
      meta: {
        pageName: '',
        noAuth: true,
      },
      component: redirects.PortfolioRedirect('profile'),
    },
    {
      path: '/product/:productId',
      name: 'product-view',
      props: true,
      meta: {
        pageName: 'Product Details',
        noAuth: true,
        theme: 'stella',
        fullWidth: true,
      },
      component: () => import('../pages/TDP/Product.vue'),
    },
    createGuardedRoute(
      {
        path: '/product/:productId/edit',
        name: 'product-edit',
        props: true,
        meta: {
          pageName: 'Product Details',
          noAuth: true,
          mode: 'edit',
        },
      },
      [
        {
          type: 'tier-guard',
          tierFeature: 'EDIT_PRODUCTS',
          allowComponent: defineAsyncComponent(
            () => import('../pages/TDP/Product.vue'),
          ),
          denyComponent: defineAsyncComponent(
            () => import('../pages/Blank/NeedUpgrade.vue'),
          ),
          loadingComponent: defineAsyncComponent(
            () => import('../pages/Blank/BlankLoading.vue'),
          ),
        },
      ],
    ),
    {
      path: '/image/:imageId',
      name: 'image-view',
      props: true,
      meta: {
        pageName: 'Image Details',
        noAuth: true,
      },
      component: () => import('../pages/TDP/Image.vue'),
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      meta: {
        pageName: 'Onboarding Form',
        navType: 'none',
        footerType: 'none',
      },
      component: () => import('../pages/Onboarding/Page.vue'),
      redirect: '/?showOnboarding=true',
      children: [
        createGuardedRoute(
          {
            path: 'basic',
            name: 'onboarding-basic',
            meta: {
              pageName: 'Onboarding Basic',
              navType: 'none',
              footerType: 'none',
            },
          },
          [
            {
              type: 'tier-guard',
              tierFeature: 'ONBOARDING_BASIC',
              allowComponent: defineAsyncComponent(
                () => import('../pages/Onboarding/Basic/Page.vue'),
              ),
              denyComponent: defineAsyncComponent(
                () => import('../pages/Home/Page.vue'),
              ),
              loadingComponent: defineAsyncComponent(
                () => import('../pages/Blank/BlankLoading.vue'),
              ),
            },
          ],
        ),
        {
          path: 'form',
          name: 'onboarding-form',
          redirect: '/?showOnboarding=true',
          meta: {
            pageName: 'Onboarding Form',
            navType: 'none',
            footerType: 'none',
          },
        },
        {
          path: 'sign',
          name: 'onboarding-sign',
          redirect: '/?showSignQuiz=true',
          meta: {
            pageName: 'Onboarding Sign',
            navType: 'none',
            footerType: 'none',
          },
        },
      ],
    },
    {
      path: '/helphub/:pathMatch(.*)*',
      name: 'helphub',
      component: redirects.HelpHubRedirect(),
      meta: {
        pageName: 'Help Hub',
        noAuth: true,
        navType: 'none',
        footerType: 'none',
      },
    },
    {
      path: '/community',
      name: 'community',
      component: redirects.CommunityRedirect(),
      meta: {
        pageName: 'Community',
        noAuth: true,
        navType: 'none',
        footerType: 'none',
      },
    },
    {
      path: '/signup',
      name: 'signup',
      alias: ['/reactivate', '/join', '/student', '/referral'],
      meta: {
        pageName: 'Sign Up',
        noAuth: true,
        theme: 'stella',
        navType: 'none',
        footerType: 'none',
      },
      component: () => import('../pages/Signup/Page.vue'),
    },
    {
      path: '/signup/:signupId/complete',
      name: 'signup-complete',
      props: true,
      meta: {
        pageName: 'Sign Up Complete',
        noAuth: true,
        theme: 'stella',
        navType: 'none',
        footerType: 'none',
      },
      component: () => import('../pages/Signup/CompletePage.vue'),
    },
    {
      path: '/require-login',
      name: 'require-login',
      meta: {
        pageName: 'Login to Spoak',
        noAuth: true,
        theme: 'stella',
        navType: 'none',
        footerType: 'none',
      },
      component: () => import('../pages/Blank/RequireLogin.vue'),
    },
    {
      path: '/login',
      name: 'login',
      meta: {
        pageName: 'Login to Spoak',
        noAuth: true,
        theme: 'stella',
        navType: 'none',
        footerType: 'none',
      },
      component: () => import('../pages/Login/Page.vue'),
    },
    {
      path: '/account/member/invite/:inviteId',
      name: 'account-invite',
      props: true,
      meta: {
        pageName: 'Team Invite',
        noAuth: true,
        theme: 'stella',
        navType: 'none',
        footerType: 'none',
      },
      component: () => import('../pages/Account/Invite/Page.vue'),
    },
    {
      path: '/import',
      name: 'import',
      alias: ['/importer'],
      meta: {
        pageName: 'Import',
        noAuth: true,
        theme: 'stella',
      },
      component: defineAsyncComponent(() => import('../pages/Import/Page.vue')),
      children: [
        {
          path: 'product',
          name: 'import-product',
          meta: {
            pageName: 'Import',
            theme: 'stella',
            fullWidth: true,
          },
          component: defineAsyncComponent(
            () => import('../pages/Import/elements/Product.vue'),
          ),
        },
        {
          path: 'image',
          name: 'import-image',
          meta: {
            pageName: 'Import',
            theme: 'stella',
            fullWidth: true,
          },
          component: defineAsyncComponent(
            () => import('../pages/Import/elements/Image.vue'),
          ),
        },
        {
          path: '',
          name: 'import-redirect',
          meta: {
            pageName: 'Import',
            theme: 'stella',
            fullWidth: true,
          },
          redirect: (to: RouteLocation) => ({
            name: 'import-product',
            params: to.params,
          }),
        },
      ],
    },
    //#region legacy routes
    {
      path: '/vizi/:viziId',
      name: 'legacy-vizi-view',
      props: true,
      meta: {
        pageName: 'Vizi',
        noAuth: true,
        stellaNavType: 'custom',
        fullWidth: true,
        footerType: 'none',
      },
      component: redirects.ViziRedirect('vizi-view'),
    },
    {
      path: '/floorplan/:viziId',
      name: 'legacy-floorplan-view',
      props: true,
      meta: {
        pageName: 'Vizi',
        noAuth: true,
        stellaNavType: 'custom',
        fullWidth: true,
        footerType: 'none',
      },
      component: redirects.ViziRedirect('vizi-view'),
    },
    {
      path: '/project/:projectId',
      name: 'legacy-project-view',
      props: true,
      meta: {
        pageName: 'Project',
        noAuth: true,
        stellaNavType: 'custom',
      },
      component: redirects.ProjectRedirect('project-view'),
    },
    {
      path: '/palette/:paletteId',
      name: 'legacy-palette-view',
      props: true,
      meta: {
        pageName: 'Color Pal',
        noAuth: true,
        stellaNavType: 'custom',
      },
      component: redirects.PaletteRedirect('palette-view'),
    },
    //#endregion
  ]

  //#region Dev Only Routes
  const devRoutes: Array<RouteRecordRaw> = []
  if (
    import.meta.env.MODE === 'development' ||
    import.meta.env.VITE_BUILD_TYPE === 'feature-slot'
  ) {
    devRoutes.push({
      path: '/_DEV_/',
      name: 'Dev Guide',
      component: () => import('/@/pages/_DEV_/Page.vue'),
      children: [
        {
          path: 'stella',
          name: 'dev-stella',
          component: () => import('/@/pages/_DEV_/Stella/Page.vue'),
          children: stellaRoutes,
          meta: {
            pageName: 'Stella',
            theme: 'stella',
            fullWidth: true,
          },
        },
        {
          path: 'classic',
          name: 'dev-classic',
          component: () => import('/@/pages/_DEV_/Classic/Page.vue'),
          children: classicRoutes,
        },
        {
          path: '',
          name: 'dev-index',
          component: () => import('/@/pages/_DEV_/elements/Index.vue'),
        },
      ],
      meta: {
        pageName: 'DEV',
      },
    })
  }
  //#endregion

  return routes.concat(devRoutes).concat({
    path: '/:catchAll(.*)',
    redirect: '/404',
    meta: { pageName: 'Not Found' },
  })
}
